/**
 * Keep the selected aircraft out from under the command panels.
 *
 * Selecting a drone on Command opens the command dock across the bottom of the
 * map, and it frequently lands on top of the very aircraft it is about: the
 * operator has to pan the map or deselect to see the thing they just selected.
 *
 * The camera moves, not the panel. The dock, the fleet rail, the right rail and
 * the timeline are where they are because muscle memory puts the operator's
 * hand on ARM without looking, and a panel that relocates or collapses to solve
 * a visibility problem has traded it for a worse one. Making the dock
 * translucent was rejected outright — an operator has to be able to READ it,
 * and a command surface that is hard to read under a bright satellite tile is
 * how the wrong button gets pressed.
 *
 * All pixel geometry, no map, so the rule can be tested without WebGL.
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Insets {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * How far the aircraft is kept from the edge of the clear area.
 *
 * The marker is 34px with a callsign label under it, so a point parked exactly
 * on the boundary still has half its identity behind the panel.
 */
const DEFAULT_MARGIN_PX = 64;

/** Below this the clear area is not usable and the camera is left alone. */
const MIN_CLEAR_PX = 120;

/**
 * Fold each panel into the edge it is docked against.
 *
 * The edge chosen is the one that costs the least screen area to reserve, not
 * the one the panel is nearest. Nearest gets full-width bars wrong: the
 * timeline is 14px from the left edge and 14px from the bottom, and reserving
 * its "nearest" edge would write off the entire left half of the map.
 */
export function insetsFor(viewport: Size, panels: Rect[]): Insets {
  const insets: Insets = { left: 0, right: 0, top: 0, bottom: 0 };

  for (const panel of panels) {
    const left = Math.max(0, Math.min(viewport.width, panel.left));
    const right = Math.max(0, Math.min(viewport.width, panel.right));
    const top = Math.max(0, Math.min(viewport.height, panel.top));
    const bottom = Math.max(0, Math.min(viewport.height, panel.bottom));
    // Off-screen, zero-sized, or a hidden panel still in the DOM.
    if (right <= left || bottom <= top) continue;

    const candidates: Array<{ side: keyof Insets; extent: number; cost: number }> = [
      { side: 'left', extent: right, cost: right * viewport.height },
      { side: 'right', extent: viewport.width - left, cost: (viewport.width - left) * viewport.height },
      { side: 'top', extent: bottom, cost: bottom * viewport.width },
      { side: 'bottom', extent: viewport.height - top, cost: (viewport.height - top) * viewport.width },
    ];

    let cheapest = candidates[0];
    if (!cheapest) continue;
    for (const candidate of candidates) {
      if (candidate.cost < cheapest.cost) cheapest = candidate;
    }
    insets[cheapest.side] = Math.max(insets[cheapest.side], cheapest.extent);
  }

  return insets;
}

/** The part of the map no panel is covering, or null when there is not enough left. */
export function clearArea(viewport: Size, panels: Rect[]): Rect | null {
  const insets = insetsFor(viewport, panels);
  const area: Rect = {
    left: insets.left,
    top: insets.top,
    right: viewport.width - insets.right,
    bottom: viewport.height - insets.bottom,
  };
  if (area.right - area.left < MIN_CLEAR_PX) return null;
  if (area.bottom - area.top < MIN_CLEAR_PX) return null;
  return area;
}

/**
 * Where the aircraft should sit on screen so the panels are not over it.
 *
 * Returns null when it is already in the clear, which is the important half:
 * the camera must not lurch every time an operator clicks a track that was
 * perfectly visible. The move is the smallest one that works — the aircraft is
 * nudged just clear of the panel rather than recentred, so the rest of the
 * picture the operator was looking at stays on screen.
 */
export function visiblePointFor(
  point: Point,
  viewport: Size,
  panels: Rect[],
  margin = DEFAULT_MARGIN_PX,
): Point | null {
  const area = clearArea(viewport, panels);
  if (!area) return null;

  const room = Math.min(area.right - area.left, area.bottom - area.top);
  const applied = Math.min(margin, room / 2 - 1);
  const left = area.left + applied;
  const right = area.right - applied;
  const top = area.top + applied;
  const bottom = area.bottom - applied;

  const x = Math.min(Math.max(point.x, left), right);
  const y = Math.min(Math.max(point.y, top), bottom);
  if (x === point.x && y === point.y) return null;
  return { x, y };
}
